import React from 'react';
import cx from 'classnames';
import {FieldValues, useController} from 'react-hook-form';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel, {FormControlLabelProps} from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import MuiRadioGroup, {RadioGroupProps as MuiRadioGroupProps} from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import {makeStyles} from '@material-ui/core';
import {RadioGroupSkeleton} from '../../';
import {UseControllerProps} from './types';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    header: {
        marginBottom: theme.spacing(2),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        textTransform: 'unset'
    },
    radioGroup: {
        '& label ~ label': {
            marginTop: theme.spacing(2)
        }
    },
    formLabel: {
        color: theme.palette.text.secondary,
        '& > span:last-child': {
            width: '100%'
        }
    }
}));

export interface RadioGroupProps<FormValues extends FieldValues>
    extends UseControllerProps<MuiRadioGroupProps, FormValues> {
    header?: string | React.ReactNode | HTMLElement;
    required?: boolean;
    options?: Optional<FormControlLabelProps, 'control'>[];
    isLoading?: boolean;
}

function RadioGroup<FormValues>({
    name,
    rules,
    control,
    defaultValue = '',
    shouldUnregister = true,
    header,
    options = [],
    children,
    required,
    isLoading,
    ...radioProps
}: React.PropsWithChildren<RadioGroupProps<FormValues>>) {
    const styles = useStyles();
    const controller = useController({control, name, defaultValue, rules, shouldUnregister});
    const {className, ...restRadioProps} = radioProps || {};
    const rootClasses = cx(styles.root, className);

    if (isLoading && options.length) {
        return <RadioGroupSkeleton className={rootClasses} />;
    }

    const {
        field: {ref, ...field},
        fieldState: {invalid, error}
    } = controller;

    return (
        <FormControl
            className={rootClasses}
            component="fieldset"
            error={invalid}
            required={required}>
            {header && (
                <FormLabel className={styles.header} component="legend">
                    {header}
                </FormLabel>
            )}
            <MuiRadioGroup
                {...restRadioProps}
                {...field}
                className={styles.radioGroup}
                innerRef={ref}>
                {options.map((option, i) => {
                    return (
                        <FormControlLabel
                            key={i}
                            className={styles.formLabel}
                            control={<Radio />}
                            {...option}
                        />
                    );
                })}
                {children}
            </MuiRadioGroup>
            <FormHelperText error={invalid}>{error && error.message}</FormHelperText>
        </FormControl>
    );
}

export default RadioGroup;
